import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Button, Checkbox, Dropdown, Input } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ResolveMultiSelectOption from './components/ResolveMultiSelctOption';
import { getIsMixed } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess/utils';
import InfiniteScroll from 'components/InfiniteScroll';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import { checkIsSelected, getSearchStringFromOption } from './utils';
import { ResolveMultiSelectProps } from './types';
import useCallbackRef from 'hooks/useCallbackRef';
import utils, {
  getArrayLength,
  getHasElementsInArray,
  getIsArrayEmpty,
  includesIgnoreCase,
  isNull,
} from 'utils';
import SearchIcon from 'icons/Search.icon';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ENTER_KEY } from 'data/global';
import useDropdownContentSize from 'hooks/useDropdownContentSize';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useMatchMedia from 'hooks/useMatchMedia';
import useModalResizeObserver from 'hooks/useModalResizeObserver';
import Counter from 'components/Counter';
import { SIZE } from 'models/Size';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';

import './ResolveMultiSelect.styles.scss';

const EMPTY_OPTION = { label: '', value: null } as any;

function ResolveMultiSelect<T>(props: ResolveMultiSelectProps<T>) {
  const { t } = useTranslation();

  const {
    className,
    value,
    disabled,
    options,
    hasError,
    renderAsPortal,
    portalClassName,
    pillText,
    hasCheckbox = false,
    hasCheckboxAll = false,
    hasSelectedOptions = true,
    searchable = true,
    emptySearchTitle = t('noDataFor'),
    searchPlaceholder = `${t('search')}...`,
    noDataTitle = t('noDataRMSTitle'),
    placeholder = searchPlaceholder,
    triggerIcon = SearchIcon,
    loading = false,
    onScroll,
    onSearchChange,
    onChange,
  } = props;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(null);

  const takeHoverInConsideration = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const isPhablet = useMatchMedia('isPhablet');

  const isPill = !!pillText;

  const classes = classNames(
    'resolve-multi-select',
    {
      'resolve-multi-select--pill': isPill,
      'resolve-multi-select--error': hasError,
      'resolve-multi-select--disabled': disabled,
      'as-portal': renderAsPortal,
    },
    className,
  );

  const portalClassNames = classNames(
    'resolve-multi-select--portal',
    portalClassName,
  );

  const wrapperClassNames = classNames('resolve-multi-select__wrapper', {
    pill: isPill,
  });

  const { dropdownContentRef } = useDropdownContentSize(
    dropdown,
    renderAsPortal || !isPhablet,
  );

  const handleDelete = useCallback(
    (val: T) => onChange(value?.filter((el) => el !== val)),
    [onChange, value],
  );

  const handleAdd = useCallback(
    (optVal: T) => {
      if (value?.every((val) => val !== optVal)) {
        onChange([...value, optVal]);
      } else {
        handleDelete(optVal);
      }
    },
    [value, handleDelete, onChange],
  );

  const renderSelectedOptions = useMemo(
    () =>
      value?.length && hasSelectedOptions ? (
        <ul className="resolve-multi-select__selected-list">
          {value.map((val) => {
            const { label, value } =
              options.find(({ value }) => value === val) || EMPTY_OPTION;

            return (
              <li key={String(val)}>
                <ResolveMultiSelectOption
                  label={label}
                  value={value}
                  disabled={disabled}
                  onDelete={handleDelete}
                />
              </li>
            );
          })}
        </ul>
      ) : null,
    [disabled, options, value, hasSelectedOptions, handleDelete],
  );

  const searchOptions = useMemo(() => {
    if (!searchable) return null;
    if (!search) return options;

    return options.filter((option) => {
      const searchValue = getSearchStringFromOption(option);

      return includesIgnoreCase(searchValue, search);
    });
  }, [options, search, searchable]);

  const finalOptions = useMemo(
    () => (!searchable ? options : searchOptions) || [],
    [options, searchOptions, searchable],
  );

  const finalOptionsLength = getArrayLength(finalOptions);

  const numberOfSelected = getArrayLength(value);
  const hasSelected = !!numberOfSelected;

  const selectableValues = useMemo(
    () =>
      finalOptions
        .filter(({ disabled }) => !disabled)
        .map(({ value }) => value),
    [finalOptions],
  );

  const allSelected = useMemo(
    () =>
      getHasElementsInArray(selectableValues) &&
      selectableValues.every((optionValue) =>
        checkIsSelected(value, optionValue),
      ),
    [selectableValues, value],
  );

  const isAllMixed = useMemo(
    () =>
      !allSelected &&
      selectableValues.some((optionValue) =>
        checkIsSelected(value, optionValue),
      ),
    [allSelected, selectableValues, value],
  );

  const Trigger = useMemo(
    () => (
      <div className="resolve-multi-select__trigger">
        {utils.renderIcon(triggerIcon)}

        <div className="resolve-multi-select__trigger__content">
          {hasSelected ? (
            <span className="resolve-multi-select__count">
              {numberOfSelected} {t('selectedLC')}
            </span>
          ) : (
            <span className="resolve-multi-select__placeholder">
              {placeholder}
            </span>
          )}
        </div>

        <ChevrondownIcon className="resolve-multi-select__chevron" />
      </div>
    ),
    [hasSelected, numberOfSelected, placeholder, t, triggerIcon],
  );

  const PillTrigger = useMemo(
    () => (
      <div
        className={classNames('resolve-multi-select__pill-trigger', {
          selected: hasSelected,
        })}
      >
        <span>{pillText}</span>
        <Counter
          size={SIZE.SMALL}
          variant={COUNTER_VARIANTS.GREY}
          count={numberOfSelected}
        />
        <ChevrondownIcon className="resolve-multi-select__chevron" />
      </div>
    ),
    [hasSelected, numberOfSelected, pillText],
  );

  const indexChange = useCallback(
    (value: number) => {
      if (value >= finalOptionsLength) {
        setActiveIndex(0);
        return;
      }
      if (value <= 0) {
        setActiveIndex(finalOptionsLength - 1);
        return;
      }

      setActiveIndex(value);
    },
    [finalOptionsLength],
  );

  const onOptionKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      switch (ev.key) {
        case ENTER_KEY:
          ev.preventDefault();

          if (!isNull(activeIndex)) handleAdd(options[activeIndex].value);
          return;
        case ARROW_DOWN_KEY:
          ev.preventDefault();

          indexChange(activeIndex + 1);
          return;
        case ARROW_UP_KEY:
          ev.preventDefault();

          indexChange(activeIndex - 1);
          return;
        default:
          return null;
      }
    },
    [activeIndex, handleAdd, indexChange, options],
  );

  const handleSelectAll = useCallback(
    (checked) => {
      if (getIsArrayEmpty(selectableValues)) return;

      const selectedValues = value || [];

      if (!checked) {
        onChange(
          selectedValues.filter(
            (selectedValue) => !selectableValues.includes(selectedValue),
          ),
        );
        return;
      }

      onChange([
        ...selectedValues,
        ...selectableValues.filter(
          (selectableValue) => !selectedValues.includes(selectableValue),
        ),
      ]);
    },
    [onChange, selectableValues, value],
  );

  useModalResizeObserver({
    enabled: renderAsPortal,
    isOpen: dropdown?.isOpen,
    rootRef,
  });

  return (
    <div ref={rootRef} className={wrapperClassNames}>
      <Dropdown
        ref={dropdownRef}
        className={classes}
        renderAsPortal={renderAsPortal}
        portalClassName={portalClassNames}
        trigger={isPill ? PillTrigger : Trigger}
        disabled={disabled}
        onKeyDown={onOptionKeyDown}
        onClose={() => setActiveIndex(null)}
      >
        <div className="resolve-multi-select__panel" ref={dropdownContentRef}>
          {searchable && (
            <Input
              className="resolve-multi-select__search"
              placeholder={searchPlaceholder}
              prefixNode={<SearchIcon />}
              value={search}
              disabled={disabled}
              onChange={({ target: { value } }) => {
                setSearch(value);
                onSearchChange?.(value);
              }}
            />
          )}

          {renderSelectedOptions}

          {hasCheckboxAll && getHasElementsInArray(selectableValues) && (
            <Checkbox
              label={t('selectAll')}
              className={classNames('select-all', { mixed: isAllMixed })}
              checked={allSelected}
              onChange={handleSelectAll}
            />
          )}

          <InfiniteScroll
            role="listbox"
            localScroll
            className="resolve-multi-select__options"
            onScroll={onScroll}
            onMouseLeave={() => setActiveIndex(null)}
            onMouseMove={() => (takeHoverInConsideration.current = true)}
          >
            {finalOptions.map((opt, index) => {
              const isSelected = checkIsSelected(value, opt.value);
              const mixed = !isSelected && getIsMixed(opt.state);
              const disabled = opt.disabled;

              return (
                <div
                  key={String(opt.value)}
                  tabIndex={-1}
                  role="option"
                  aria-selected={isSelected}
                  className={classNames(
                    'resolve-multi-select__options__option',
                    {
                      'resolve-multi-select__options__option--selected':
                        isSelected,
                      'resolve-multi-select__options__option--active-index':
                        activeIndex === index,
                      'resolve-multi-select__options__option--checkbox':
                        hasCheckbox,
                      'resolve-multi-select__options__option--disabled':
                        disabled,
                      'rendered-in-portal': renderAsPortal,
                    },
                  )}
                  onFocus={() => setActiveIndex(index)}
                  onMouseEnter={() => {
                    if (takeHoverInConsideration.current) {
                      takeHoverInConsideration.current = false;
                      setActiveIndex(index);
                    }
                  }}
                  {...(!hasCheckbox &&
                    !disabled && { onClick: () => handleAdd(opt.value) })}
                >
                  {hasCheckbox ? (
                    <Checkbox
                      label={opt.label}
                      checked={isSelected}
                      disabled={disabled}
                      className={classNames({ mixed })}
                      onChange={() => handleAdd(opt.value)}
                    />
                  ) : (
                    opt.label
                  )}
                </div>
              );
            })}

            {!finalOptionsLength && (
              <div className="resolve-multi-select__empty">
                {loading && search && t('isLoading')}
                {!loading && !search && noDataTitle}
                {!loading && search && (
                  <>
                    {emptySearchTitle} <span>{search}</span>
                  </>
                )}
              </div>
            )}
          </InfiniteScroll>

          <div className="resolve-multi-select__footer">
            <Button
              variant="link"
              disabled={getIsArrayEmpty(value)}
              onClick={() => {
                dropdown?.setIsOpen(false);
                onChange([]);
              }}
            >
              {t('clearAll')}
            </Button>

            {search && getHasElementsInArray(finalOptions) && (
              <p>{t('numberOfMatches', { count: finalOptionsLength })}</p>
            )}
          </div>
        </div>
      </Dropdown>
    </div>
  );
}

export default ResolveMultiSelect;
